import type { FC } from 'react';

import { Card, CardContent, CardHeader, Chip, Stack, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { UserAvatar } from 'components/data-display/avatar/UserAvatar';
import { BlueprintIcon } from 'components/icons/blueprint/BlueprintIcon';
import type {
  EventHistoryInfo,
  NodeInfo
} from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistory.types';
import { EventHistoryDiffText } from 'pages/projects/projectPage/selectedProject/projectHistory/EventHistoryDiffText';

export enum EventLogOperation {
  created,
  updated,
  deleted
}

export interface EventHistoryCardProps {
  eventHistoryInfo: EventHistoryInfo;
  targetNode: NodeInfo;
}

export const EventHistoryCard: FC<EventHistoryCardProps> = ({ eventHistoryInfo, targetNode }) => {
  const { firstName, lastName, email, avatar } = eventHistoryInfo.user;
  const { operation, value, transactionId, oldValue } = eventHistoryInfo;

  const hoursMinutes = new Date(Date.parse(eventHistoryInfo.timeStamp)).toLocaleTimeString('default', {
    hour: '2-digit',
    minute: '2-digit'
  });

  const userFullName = firstName + ' ' + lastName;
  const userFullNameTrimmed = userFullName.trim();
  const userFullNameToDisplay = userFullNameTrimmed === '' ? email : userFullNameTrimmed;

  const { icon, fieldName, fieldPropertyDisplayName, fieldPropertyType } = targetNode;

  const useStyles = makeStyles(() => ({
    cardHeaderAction: {
      margin: 'auto'
    },
    cardHeaderRoot: {
      padding: 0
    }
  }));
  const classes = useStyles();

  return (
    <Card
      key={transactionId}
      sx={(theme) => ({
        my: '4px',
        p: '16px',
        minWidth: '240px',
        maxWidth: '360px',
        background: theme.palette.background.default,
        // Border and radius come from the outlined Card variant now; this used to hand-roll them.
        '.MuiCardHeader-avatar': {
          mr: '8px'
        }
      })}
    >
      <CardHeader
        classes={{
          root: classes.cardHeaderRoot,
          action: classes.cardHeaderAction
        }}
        avatar={<UserAvatar tooltip={userFullNameToDisplay} value={avatar} size='small' />}
        action={
          <Typography variant='body2' color='text.secondary'>
            {hoursMinutes}
          </Typography>
        }
        title={
          <Stack direction='row'>
            {userFullNameToDisplay}
            <Typography variant='body2' color='text.secondary'>{`\u00a0 ${EventLogOperation[operation]}`}</Typography>
          </Stack>
        }
      />
      <CardContent
        sx={{
          pb: 0,
          pt: '12px',
          px: 0,
          '&:last-child': {
            paddingBottom: 0
          }
        }}
      >
        <Chip
          size='small'
          sx={(theme) => ({
            height: '20px',
            mb: '8px',
            '&:hover': { backgroundColor: theme.palette.colors.steel[200] }
          })}
          icon={<BlueprintIcon name={icon} fontSize='small' />}
          label={
            <Stack direction='row' alignItems='center'>
              <Typography variant='body2'>{`${fieldName}`}</Typography>

              <Typography variant='body2' color='text.secondary'>
                {`\u00a0 - ${fieldPropertyDisplayName}`}
              </Typography>
            </Stack>
          }
        />

        <EventHistoryDiffText
          newText={value}
          oldText={oldValue}
          keyPrefix={transactionId}
          fieldPropertyType={fieldPropertyType}
        />
      </CardContent>
    </Card>
  );
};
